import { type RequestPayload, router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Divider, Fieldset, Select, SimpleGrid, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { getFullName } from "../../helpers/stringHelper";
import type { Apprentice } from "../../models/apprentice";
import type { ApprenticeSupervisor } from "../../models/apprenticeSupervisor";
import type { Company } from "../../models/company";
import { getOccupationOptions, type Occupation } from "../../models/enums/occupation";
import { type Group, getGroupOptions } from "../../models/group";
import type { School } from "../../models/school";
import { getYearContextOptions, type YearContext } from "../../models/yearContext";
import { SaveButton } from "../button/SaveButton";
import { type ParentErrors, ParentForm, type ParentFormType } from "../parent/ParentForm";
import { ParticularsForm, type ParticularsFormErrorType, type ParticularsFormType } from "../particularsForm/ParticularsForm";
import { type SchoolDayError, SchoolDayForm, type SchoolDayFormType } from "../schoolDay/SchoolDayForm";
import { FilterSelect } from "../select/FilterSelect";
import { UserForm, type UserFormErrorTypes, type UserFormType } from "../user/UserForm";

type ApprenticeFormProps = {
  apprentice?: Apprentice;
  submitButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
  apprentice_supervisors: ApprenticeSupervisor[];
  companies: Company[];
  yearContexts: YearContext[];
  groups: Group[];
  schools: School[];
};

type ApprenticeFormType = UserFormType &
  SchoolDayFormType &
  ParticularsFormType &
  ParentFormType & {
    short?: string;
    occupation?: Occupation;
    responsible_apprentice_supervisor_id?: string;
    company_id?: string;
    year_context_id?: string;
    group_id?: string;
  };

type FormErrors = UserFormErrorTypes &
  ParticularsFormErrorType & {
    short?: string[];
    occupation?: string[];
    company?: string[];
    year_context?: string[];
    responsible_apprentice_supervisor?: string[];
    school_errors?: SchoolDayError[];
    parent_errors?: ParentErrors[];
  };

export function ApprenticeForm({
  apprentice,
  apprentice_supervisors,
  companies,
  onSubmit,
  submitButtonLabel,
  yearContexts,
  groups,
  schools,
}: ApprenticeFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const supervisorOptions = apprentice_supervisors.map((apprentice_supervisor) => ({
    label: getFullName(apprentice_supervisor),
    value: apprentice_supervisor.id.toString(),
  }));

  const companyOptions = companies.map((company) => ({
    label: company.name,
    value: company.id.toString(),
  }));

  const initialSchoolDayValues = apprentice?.school_days?.length
    ? apprentice.school_days.map((day) => ({ ...day, school_id: day.school.id.toString() }))
    : [];

  const initialParentValues = apprentice?.parents?.length ? apprentice.parents : [];

  const form = useForm<ApprenticeFormType>({
    mode: "uncontrolled",
    initialValues: {
      salutation: apprentice?.salutation,
      first_name: apprentice?.first_name,
      last_name: apprentice?.last_name,
      short: apprentice?.short,
      occupation: apprentice?.occupation,
      responsible_apprentice_supervisor_id: apprentice?.apprentice_supervisor?.id.toString(),
      company_id: apprentice?.company.id.toString(),
      school_days_attributes: initialSchoolDayValues,
      year_context_id: apprentice?.year_context.id.toString(),
      group_id: apprentice?.group?.id.toString(),
      user_attributes: {
        email: apprentice?.user.email,
        password: "",
        active: apprentice?.user.active,
      },
      parents_attributes: initialParentValues,
    },
  });

  form.watch("year_context_id", ({ value }) => reloadGroups(value, form.getValues().occupation));
  form.watch("occupation", ({ value }) => reloadGroups(form.getValues().year_context_id, value));

  const [groupSelected, setGroupSelected] = useState(!!form.values.group_id);
  form.watch("group_id", ({ value }) => setGroupSelected(!!value));

  const reloadGroups = (yearContextId?: string, occupation?: Occupation) =>
    router.reload({
      only: ["groups"],
      data: { selected_year_context_id: yearContextId, selected_occupation: occupation },
    });

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Stack gap="xl">
        <SimpleGrid cols={2}>
          <Fieldset legend="Details">
            <Stack>
              <ParticularsForm errors={errors} form={form} />
              <TextInput label="Kürzel" key={form.key("short")} {...form.getInputProps("short")} error={errors?.short} />
              <UserForm errors={errors} form={form} />
              <Stack>
                <Select
                  withAsterisk
                  label="Jahrgang"
                  clearable
                  data={getYearContextOptions(yearContexts)}
                  key={form.key("year_context_id")}
                  {...form.getInputProps("year_context_id")}
                  error={errors?.year_context}
                  disabled={groupSelected}
                />
                <Select
                  withAsterisk
                  label="Beruf"
                  clearable
                  data={getOccupationOptions()}
                  key={form.key("occupation")}
                  {...form.getInputProps("occupation")}
                  error={errors?.occupation}
                  disabled={groupSelected}
                />
                <Select
                  label="Gruppe"
                  clearable
                  nothingFoundMessage="Keine Gruppen..."
                  data={getGroupOptions(groups ?? [], false)}
                  key={form.key("group_id")}
                  {...form.getInputProps("group_id")}
                />
                <FilterSelect
                  withAsterisk
                  data={companyOptions}
                  key={form.key("company_id")}
                  clearable
                  label="Lehrbetrieb"
                  {...form.getInputProps("company_id")}
                  error={errors?.company}
                />
                <FilterSelect
                  data={supervisorOptions}
                  key={form.key("responsible_apprentice_supervisor_id")}
                  clearable
                  label="Berufsbildner/in"
                  {...form.getInputProps("responsible_apprentice_supervisor_id")}
                  error={errors?.responsible_apprentice_supervisor}
                />
              </Stack>
            </Stack>
          </Fieldset>
          <Stack>
            <Fieldset legend="Eltern">
              <ParentForm form={form} errors={errors?.parent_errors} />
            </Fieldset>
            <Fieldset legend="Schule">
              <SchoolDayForm schools={schools} form={form} errors={errors?.school_errors} />
            </Fieldset>
          </Stack>
        </SimpleGrid>
        <Divider />
        <SaveButton label={submitButtonLabel} />
      </Stack>
    </form>
  );
}
